import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { ChevronDown } from 'lucide-react'
import { findRegion, matchRegions, regionLabelOf, regionNameOf, regionsOf, type Region } from '../regions.js'

interface RegionInputProps {
  id?: string
  /** ISO 3166-1 alpha-2: which country's divisions to offer. */
  country: string
  /** As the address stores it: the division's code (`NY`) for a listed country, the text typed for any other. */
  value: string
  /** Called with the code once a division is chosen or typed exactly, else with the text as typed, which `isKnownRegion` refuses. */
  onChange: (value: string) => void
  className?: string
  disabled?: boolean
}

/**
 * A state (or province) field that finds the division as the person types
 * its name: "new" lists the four News, "car" both Carolinas, "ny" New York.
 * It is a WAI-ARIA 1.2 combobox: the arrow keys move through the list,
 * Enter chooses, Escape closes, and leaving the field with only one
 * division matching chooses that one. The field shows the name and stores
 * the code. A part-typed or unknown name is marked, and the browser will
 * not submit the form with it. For a country the platform does not list
 * yet (`REGION_CATALOGUE`), it is an ordinary text field.
 */
export function RegionInput({ id, country, value, onChange, className = '', disabled }: RegionInputProps) {
  const listed = regionsOf(country)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()
  const hintId = useId()
  const [text, setText] = useState(() => regionNameOf(country, value))
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [touched, setTouched] = useState(false)
  const matches = open && listed ? matchRegions(country, text) : []
  const unknown = listed !== null && text.trim() !== '' && findRegion(country, text) === null
  const label = regionLabelOf(country)

  // A value set from outside (a saved address read back, a reset) is shown by name, unless the person is typing.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setText(regionNameOf(country, value))
    }
  }, [country, value])

  useEffect(() => {
    inputRef.current?.setCustomValidity(unknown ? `Choose a ${label.toLowerCase()} from the list.` : '')
  }, [unknown, label])

  const activeCode = matches[active]?.code
  useEffect(() => {
    if (activeCode) {
      document.getElementById(`${listId}-${activeCode}`)?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeCode, listId])

  if (!listed) {
    return <input id={id} className={className} value={value} disabled={disabled} autoComplete="address-level1" onChange={(event) => onChange(event.target.value)} />
  }

  const choose = (region: Region) => {
    setText(region.name)
    setOpen(false)
    onChange(region.code)
  }

  const handleType = (typed: string) => {
    setText(typed)
    setOpen(true)
    setActive(0)
    onChange(findRegion(country, typed)?.code ?? typed)
  }

  const handleBlur = () => {
    setOpen(false)
    setTouched(true)
    const exact = findRegion(country, text)
    const only = text.trim() === '' ? [] : matchRegions(country, text)
    const settled = exact ?? (only.length === 1 ? only[0] : undefined)
    if (settled) {
      choose(settled)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        setActive(0)
        return
      }
      const count = matches.length
      if (count > 0) {
        setActive((index) => (event.key === 'ArrowDown' ? (index + 1) % count : (index - 1 + count) % count))
      }
    } else if (event.key === 'Enter' && open && matches[active]) {
      event.preventDefault()
      choose(matches[active])
    } else if (event.key === 'Escape' && open) {
      event.preventDefault()
      setOpen(false)
    }
  }

  const activeOption = open ? matches[active] : undefined
  const showHint = touched && !open && unknown

  return (
    <span className="relative flex min-w-0 flex-col">
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={activeOption ? `${listId}-${activeOption.code}` : undefined}
        aria-invalid={showHint || undefined}
        aria-describedby={showHint ? hintId : undefined}
        autoComplete="address-level1"
        placeholder={`Start typing a ${label.toLowerCase()}`}
        className={`${className} pr-8${showHint ? ' border-coral' : ''}`}
        value={text}
        disabled={disabled}
        onChange={(event) => handleType(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={`Show every ${label.toLowerCase()}`}
        disabled={disabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          setOpen((wasOpen) => !wasOpen)
          setActive(0)
          inputRef.current?.focus()
        }}
        className="absolute right-2 top-2 p-0.5 text-ink-mid hover:text-marine disabled:opacity-50"
      >
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      <ul
        id={listId}
        role="listbox"
        aria-label={label}
        hidden={!open || matches.length === 0}
        className="absolute left-0 right-0 top-full z-30 mt-1 max-h-60 overflow-auto rounded border border-marine/25 bg-white py-1 text-sm shadow-lg"
      >
        {matches.map((region, index) => (
          <li
            key={region.code}
            id={`${listId}-${region.code}`}
            role="option"
            aria-selected={index === active}
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setActive(index)}
            onClick={() => choose(region)}
            className={`flex cursor-pointer items-center justify-between gap-3 px-2.5 py-1.5 ${index === active ? 'bg-marine/10 text-marine' : 'text-ink'}`}
          >
            <span>{region.name}</span>
            <span className="text-[11px] font-bold text-ink-mid">{region.code}</span>
          </li>
        ))}
      </ul>
      {open && matches.length === 0 && (
        <span role="status" className="absolute left-0 right-0 top-full z-30 mt-1 rounded border border-marine/25 bg-white px-2.5 py-1.5 text-[11px] text-ink-mid shadow-lg">
          No {label.toLowerCase()} matches "{text.trim()}".
        </span>
      )}
      {showHint && (
        <span id={hintId} className="mt-1 text-[11px] text-coral">
          Choose a {label.toLowerCase()} from the list.
        </span>
      )}
    </span>
  )
}
